import { type ConditionProperties } from 'json-rules-engine';
import {
  getGroupConditions,
  type RuleTopLevelCondition,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import { type OptimizationEditorStep } from '@bbkAdminComponents/optimizer/OptimizationEditorStepEnum';
import {
  OptimizationActionType,
  OptimizationAddonScope,
  OptimizationPlanScope,
  OptimizationSliceHelper,
  type Optimization,
  type OptimizationAction,
} from '@bbkAdminRedux/rtkq/optimizations.slice';

type PartialPlanPriceAdjustmentAction = {
  adjustment_type?: unknown;
  adjustment_value?: unknown;
  plan_adjustments?: Array<{
    plan_id?: string;
    adjustment_type?: unknown;
    adjustment_value?: unknown;
  }>;
};

export class OptimizationEditorHelper {
  private static isSpecificPlanAdjustmentComplete(planAdjustment: {
    plan_id?: string;
    adjustment_type?: unknown;
    adjustment_value?: unknown;
  }): boolean {
    return (
      !!planAdjustment.plan_id?.trim() &&
      planAdjustment.adjustment_type !== undefined &&
      planAdjustment.adjustment_value !== undefined
    );
  }

  private static hasDiscardableSpecificPlanAdjustmentData(
    action: PartialPlanPriceAdjustmentAction
  ): boolean {
    const seenPlanIds = new Set<string>();

    return (
      action.plan_adjustments?.some((planAdjustment) => {
        const planId = planAdjustment.plan_id?.trim();
        if (
          !planId ||
          seenPlanIds.has(planId) ||
          !OptimizationEditorHelper.isSpecificPlanAdjustmentComplete(
            planAdjustment
          )
        ) {
          return false;
        }

        seenPlanIds.add(planId);
        return true;
      }) ?? false
    );
  }

  private static areSpecificPlanAdjustmentsComplete(
    action: PartialPlanPriceAdjustmentAction
  ): boolean {
    const planAdjustments = action.plan_adjustments ?? [];
    if (planAdjustments.length === 0) {
      return false;
    }

    const seenPlanIds = new Set<string>();
    return planAdjustments.every((planAdjustment) => {
      const planId = planAdjustment.plan_id?.trim();
      if (
        !planId ||
        seenPlanIds.has(planId) ||
        !OptimizationEditorHelper.isSpecificPlanAdjustmentComplete(
          planAdjustment
        )
      ) {
        return false;
      }

      seenPlanIds.add(planId);
      return true;
    });
  }

  static hasConfiguredActionPayload(
    action: Partial<OptimizationAction> | undefined
  ): boolean {
    if (!action?.type) {
      return false;
    }

    if (action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT) {
      const planScope = OptimizationSliceHelper.getPlanScope(action);
      if (planScope === OptimizationPlanScope.ALL_PLANS) {
        return (
          action.adjustment_type !== undefined &&
          action.adjustment_value !== undefined
        );
      }

      const planAdjustments = action.plan_adjustments ?? [];
      if (planAdjustments.length === 0) {
        return false;
      }

      return OptimizationEditorHelper.hasDiscardableSpecificPlanAdjustmentData(
        action as PartialPlanPriceAdjustmentAction
      );
    }

    if (action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT) {
      if (!action.scope) {
        return false;
      }
      if (action.scope === OptimizationAddonScope.ALL_ADDONS) {
        return (
          action.adjustment_type !== undefined &&
          action.adjustment_value !== undefined
        );
      }
      const addons = action.addons ?? [];
      return (
        addons.length > 0 &&
        addons.every(
          (addon) =>
            !!addon.item_price_id?.trim() &&
            addon.adjustment_type !== undefined &&
            addon.adjustment_value !== undefined
        )
      );
    }

    if (action.type === OptimizationActionType.PLAN_CHANGE) {
      return (
        (action.plans?.length ?? 0) > 0 &&
        action.plans!.every(
          (plan) =>
            !!plan.current_plan &&
            !!plan.target_plan &&
            plan.current_plan !== plan.target_plan
        )
      );
    }

    return true;
  }

  private static hasCompleteActionPayload(
    action: Partial<OptimizationAction> | undefined
  ): boolean {
    if (!action?.type) {
      return false;
    }

    if (action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT) {
      const planScope = OptimizationSliceHelper.getPlanScope(action);
      if (planScope === OptimizationPlanScope.ALL_PLANS) {
        return (
          action.adjustment_type !== undefined &&
          action.adjustment_value !== undefined
        );
      }

      return OptimizationEditorHelper.areSpecificPlanAdjustmentsComplete(
        action as PartialPlanPriceAdjustmentAction
      );
    }

    return OptimizationEditorHelper.hasConfiguredActionPayload(action);
  }

  static getNextButtonText(
    curStep: OptimizationEditorStep
  ): string | undefined {
    switch (curStep) {
      case 'audience':
        return 'Continue to Actions';
      case 'actions':
        return 'Continue to Configurations';
      case 'configurations':
        return 'Continue to Review';
      case 'review':
        return 'Publish Optimization';
      case 'publish':
        return undefined;
      default:
        return 'Continue';
    }
  }

  static getNextStep(
    curStep: OptimizationEditorStep
  ): OptimizationEditorStep | undefined {
    switch (curStep) {
      case 'audience':
        return 'actions';
      case 'actions':
        return 'configurations';
      case 'configurations':
        return 'review';
      case 'review':
        return 'publish';
      case 'publish':
        return undefined;
      default:
        return undefined;
    }
  }

  static getPrevStep(
    curStep: OptimizationEditorStep
  ): OptimizationEditorStep | undefined {
    switch (curStep) {
      case 'audience':
        return undefined;
      case 'actions':
        return 'audience';
      case 'configurations':
        return 'actions';
      case 'review':
        return 'configurations';
      case 'publish':
        return 'review';
      default:
        return undefined;
    }
  }

  private static isConditionComplete(condition: ConditionProperties): boolean {
    if (!condition.fact || !condition.operator) {
      return false;
    }
    // if operator is "is empty" or "is not empty", no value is needed
    if (
      condition.operator === 'Generic.isNilOrEmpty' ||
      condition.operator === 'Generic.isNotNilOrEmpty'
    ) {
      return true;
    }
    const value = condition.value;
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return !!value;
  }

  static isAudienceComplete(optimization: Partial<Optimization>): boolean {
    const audience = optimization.audience as RuleTopLevelCondition | undefined;
    if (!audience) return false;
    const conditions = getGroupConditions(audience);
    if (!conditions.length) return false;
    return conditions.every(OptimizationEditorHelper.isConditionComplete);
  }

  static areActionsComplete(optimization: Partial<Optimization>): boolean {
    const actions = optimization.actions;
    if (!actions || 0 >= actions.length) {
      return false;
    }
    return actions.every((action) => {
      if (
        !action.display_name ||
        action.display_name.trim() === '' ||
        !action.condition
      ) {
        return false;
      }
      const conditions = getGroupConditions(
        action.condition as RuleTopLevelCondition
      );
      if (!conditions.every(OptimizationEditorHelper.isConditionComplete)) {
        return false;
      }
      return OptimizationEditorHelper.hasCompleteActionPayload(action);
    });
  }

  static areConfigsComplete(optimization: Partial<Optimization>): boolean {
    const config = optimization.configurations;
    if (!config) return false;
    return !!config.mid_cycle_billing && !!config.scheduled_changes_handling;
  }

  static isStepValid(
    step: OptimizationEditorStep,
    optimization: Partial<Optimization>
  ): boolean {
    switch (step) {
      case 'audience':
        return OptimizationEditorHelper.isAudienceComplete(optimization);
      case 'actions':
        return OptimizationEditorHelper.areActionsComplete(optimization);
      case 'configurations':
        return OptimizationEditorHelper.areConfigsComplete(optimization);
      case 'review':
      case 'publish':
        return (
          OptimizationEditorHelper.isAudienceComplete(optimization) &&
          OptimizationEditorHelper.areActionsComplete(optimization) &&
          OptimizationEditorHelper.areConfigsComplete(optimization)
        );
      default:
        return false;
    }
  }

  /**
   * Determines the initial step for the optimization editor based on the optimization configuration.
   * The step is inferred from which fields are present:
   * - If audience is not present → 'audience'
   * - If audience is present but actions is not → 'actions'
   * - If actions is present but configurations is not → 'configurations'
   * - If configurations are present → 'review'
   */
  static getInitialStep(optimization: Optimization): OptimizationEditorStep {
    // Check if audience is present and non-empty
    // The data from getOptimizations may have these as JSON strings
    const hasAudience = optimization.audience;

    // Check if actions is present and non-empty
    const hasActions = optimization.actions;

    // Check if configurations is present and non-empty
    const hasConfigurations = optimization.configurations;

    if (!hasAudience) {
      return 'audience';
    }
    if (!hasActions) {
      return 'actions';
    }
    if (!hasConfigurations) {
      return 'configurations';
    }
    return 'review';
  }
}
